import { IVector3 } from '../../types';
import { ConfigParser } from '../ConfigParser';

export enum FishRarity {
	COMMON = 'common',
	RARE = 'rare',
	TROPHY = 'trophy',
	FORBIDDEN = 'forbidden',
}

export class HobbyFishingConfig {
	public readonly blipSprite: number;
	public readonly blipColor: number;
	public readonly blipText: string;

	public readonly commonLocationBlipColor: number;
	public readonly activeLocationBlipColor: number;

	public readonly secretLocationsUpdateIntervalHours: number;
	public readonly secretLocationsLifetimeMinutes: number;

	public readonly locations: HobbyFishingLocationConfig[] = [];
	public readonly fishes: HobbyFishingFishConfig[] = [];
	public readonly fishesByLocationId = new Map<string, HobbyFishingFishChanceConfig[]>();
	public readonly fishesByBaitId = new Map<string, HobbyFishingBaitConfig[]>();

	public readonly minigame: HobbyFishingMinigameConfig;

	// public readonly locationIdToChance = new Map<string, IFishCatchChance[]>();
	// public readonly baitItemIdToChance = new Map<string, IFishCatchChance[]>();

	constructor(tables: Record<string, any>) {
		const rawCommon = tables['common'];

		this.blipSprite = ConfigParser.getInt(rawCommon, 'blip_sprite');
		this.blipColor = ConfigParser.getInt(rawCommon, 'blip_color');
		this.blipText = ConfigParser.getString(rawCommon, 'blip_text');

		this.commonLocationBlipColor = ConfigParser.getInt(rawCommon, 'common_location_blip_color');
		this.activeLocationBlipColor = ConfigParser.getInt(rawCommon, 'active_location_blip_color');

		this.secretLocationsUpdateIntervalHours = ConfigParser.getInt(
			rawCommon,
			'secret_locations_update_interval_hours',
		);
		this.secretLocationsLifetimeMinutes = ConfigParser.getInt(rawCommon, 'secret_locations_lifetime_minutes');

		const rawLocations = tables['locations'];
		for (const rawLocation of rawLocations) {
			this.locations.push(new HobbyFishingLocationConfig(rawLocation));
		}

		const rawFishes = tables['fish'];
		for (const rawFish of rawFishes) {
			this.fishes.push(new HobbyFishingFishConfig(rawFish));
		}

		const rawFishLocations = tables['fish_locations'];
		for (const rawFishLocation of rawFishLocations) {
			const fishLocationConfig = new HobbyFishingFishChanceConfig(rawFishLocation);
			const fishes = this.fishesByLocationId.get(fishLocationConfig.locationId) ?? [];

			fishes.push(fishLocationConfig);

			this.fishesByLocationId.set(fishLocationConfig.locationId, fishes);
		}

		const rawBaits = tables['baits'];
		for (const rawBait of rawBaits) {
			const baitConfig = new HobbyFishingBaitConfig(rawBait);
			const fishes = this.fishesByBaitId.get(baitConfig.baitId) ?? [];

			fishes.push(baitConfig);

			this.fishesByBaitId.set(baitConfig.baitId, fishes);
		}

		this.minigame = new HobbyFishingMinigameConfig(tables['minigame']);
	}

	public getLocationById(id: string): HobbyFishingLocationConfig | null {
		return this.locations.find((e) => e.id === id) ?? null;
	}

	public getLocationByIdOrThrow(id: string): HobbyFishingLocationConfig {
		const locationConfig = this.getLocationById(id);

		if (!locationConfig) {
			throw new Error(`Location with id "${id}" was not found`);
		}

		return locationConfig;
	}

	private getFishById(id: string): HobbyFishingFishConfig | null {
		return this.fishes.find((e) => e.fishId === id) ?? null;
	}

	public getFishByIdOrThrow(id: string): HobbyFishingFishConfig {
		const fishConfig = this.getFishById(id);

		if (!fishConfig) {
			throw new Error(`Fish with id "${id}" was not found`);
		}

		return fishConfig;
	}

	public getFishByLocationIdOrThrow(id: string): HobbyFishingFishChanceConfig[] {
		const fishes = this.fishesByLocationId.get(id) ?? [];

		if (fishes.length === 0) {
			throw new Error(`No fishes associated with location id "${id}" were found`);
		}

		return fishes;
	}

	public getFishByBaitIdOrThrow(id: string): HobbyFishingBaitConfig[] {
		const fishes = this.fishesByBaitId.get(id) ?? [];

		if (fishes.length === 0) {
			throw new Error(`No fishes associated with bait id "${id}" were found`);
		}

		return fishes;
	}

	public getClientConfig() {
		return {
			locations: this.locations.map((loc) => loc.getClientConfig()),

			blipSprite: this.blipSprite,
			blipColor: this.blipColor,
			blipText: this.blipText,

			commonLocationBlipColor: this.commonLocationBlipColor,
			activeLocationBlipColor: this.activeLocationBlipColor,

			fishes: this.fishes.map((e) => e.getClientConfig()),

			minigame: this.minigame,
		};
	}
}

export class HobbyFishingLocationConfig {
	public readonly id: string;
	public readonly minLevel: number;
	public readonly position: IVector3;
	public readonly radius: number;
	public readonly height: number;

	public readonly isSecret: boolean;

	constructor(config: Record<string, any>) {
		this.id = ConfigParser.getString(config, 'id');
		this.minLevel = ConfigParser.getInt(config, 'min_level');
		this.position = ConfigParser.getVector3(config, 'position');
		this.radius = ConfigParser.getFloat(config, 'radius');
		this.height = ConfigParser.getFloat(config, 'height');

		this.isSecret = ConfigParser.getBool(config, 'is_secret');
	}

	public getClientConfig() {
		return {
			id: this.id,
			minLevel: this.minLevel,
			isSecret: this.isSecret,
			position: this.position,
			radius: this.radius,
			height: this.height,
		};
	}
}

export class HobbyFishingFishConfig {
	public readonly fishId: string;
	public readonly rarity: FishRarity;
	public readonly description: string;
	public readonly weightMin: number;
	public readonly weightMax: number;
	public readonly expCatch: number;
	public readonly expRelease: number;

	constructor(config: Record<string, any>) {
		this.fishId = ConfigParser.getString(config, 'fish_id');
		this.rarity = ConfigParser.getEnum(config, 'rarity', FishRarity);
		this.description = ConfigParser.getString(config, 'description');
		this.weightMin = ConfigParser.getFloat(config, 'weight_min');
		this.weightMax = ConfigParser.getFloat(config, 'weight_max');
		this.expCatch = ConfigParser.getInt(config, 'exp_catch');
		this.expRelease = ConfigParser.getInt(config, 'exp_release');
	}

	public getClientConfig() {
		return {
			fishId: this.fishId,
			rarity: this.rarity,
			description: this.description,
			expCatch: this.expCatch,
			expRelease: this.expRelease,
		};
	}
}

export class HobbyFishingFishChanceConfig {
	public readonly locationId: string;
	public readonly fishId: string;
	public readonly dropChance: number;

	constructor(config: Record<string, any>) {
		this.locationId = ConfigParser.getString(config, 'location_id');
		this.fishId = ConfigParser.getString(config, 'fish_id');
		this.dropChance = ConfigParser.getFloat(config, 'drop_chance');
	}
}

export class HobbyFishingBaitConfig {
	public readonly baitId: string;
	public readonly fishId: string;
	public readonly multiplier: number;

	constructor(config: Record<string, any>) {
		this.baitId = ConfigParser.getString(config, 'bait_id');
		this.fishId = ConfigParser.getString(config, 'fish_id');
		this.multiplier = ConfigParser.getFloat(config, 'chance_multiplier');
	}
}

export class HobbyFishingMinigameConfig {
	public readonly fishEscapeChance: number;

	public readonly waitGreenSecMin: number;
	public readonly waitGreenSecMax: number;
	public readonly waitOrangeSecMin: number;
	public readonly waitOrangeSecMax: number;
	public readonly waitOtherSecMin: number;
	public readonly waitOtherSecMax: number;

	constructor(config: Record<string, any>) {
		this.fishEscapeChance = ConfigParser.getFloat(config, 'fish_escape_chance');
		this.waitGreenSecMin = ConfigParser.getInt(config, 'wait_green_sec_min');
		this.waitGreenSecMax = ConfigParser.getInt(config, 'wait_green_sec_max');
		this.waitOrangeSecMin = ConfigParser.getInt(config, 'wait_orange_sec_min');
		this.waitOrangeSecMax = ConfigParser.getInt(config, 'wait_orange_sec_max');
		this.waitOtherSecMin = ConfigParser.getInt(config, 'wait_other_sec_min');
		this.waitOtherSecMax = ConfigParser.getInt(config, 'wait_other_sec_max');
	}
}
